import { CircularProgress, styled } from '@mui/material';

import { useParams } from 'react-router';

import { useState } from 'react';

import { Typography, Box, Card, PageContent, Button, Alert } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { RobotIcon, UsersIcon } from '@app-velocity/icons';
import {
  useGetSharedScenario,
  useGetSharedScenarioAiLatestReviewQuery,
} from '@app-velocity/modules/scenarios';

import { MessageBus } from '@app-velocity/message-bus';
import { SimulationStepsLimitNotice } from '@app-velocity/libs/shared';

import { usePrototypePermissions } from '../../hooks/usePrototypePermissions';

const ConfidenceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: `${theme.spacing(10)} 0`,
  gap: theme.spacing(10),
  maxWidth: 900,
  margin: '0 auto',
}));

const Cards = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(10),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(5),
  padding: `${theme.spacing(10)} ${theme.spacing(5)}`,
  borderRadius: theme.spacing(4),
  fontSize: 16,
  alignItems: 'center',
  textAlign: 'center',
  justifyContent: 'space-between',
}));

const IconContainer = styled('div')(({ theme }) => ({
  svg: {
    fill: '#1b1b1b',
    height: theme.spacing(10),
    width: theme.spacing(10),
  },
}));

const StyledText = styled(Typography)({
  fontSize: 16,
});

const InProgressInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  color: '#7a7a7a',
  fontSize: 14,
  textAlign: 'left',
  width: '100%',
}));

const ProgressContainer = styled('div')(({ theme }) => ({
  flex: '0 0 auto',
}));

const Options = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flex: 1,
  width: '100%',
}));

const StyledOption = styled('div')<{ active: boolean }>(
  ({ theme, active }) => ({
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    alignItems: 'center',
    height: theme.spacing(8),
    borderRadius: theme.spacing(4),
    color: active ? '#1B1B1B' : '#7A7A7A',
    background: active ? 'var(--button-button-primary-color)' : '#EFEFEF',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: active
      ? 'var(--button-button-primary-stroke-color)'
      : '#EFEFEF',
  })
);

const messageBus = new MessageBus('prototypes');

export const Confidence = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { data: scenario } = useGetSharedScenario(shareToken || '', 'report');

  const [amountToRun, setAmountToRun] = useState(1);

  const { canRunSimulations, teamId } = usePrototypePermissions(
    shareToken || ''
  );
  const { t } = useI18n();
  const { data } = useGetSharedScenarioAiLatestReviewQuery(shareToken || '');

  const testWithHumans = () => {
    if (!shareToken || !scenario) return;
    messageBus.publish('modal:share-scenario:open', {
      shareToken: scenario.shareToken || '',
      prototypeId: scenario.id,
    });
  };

  const handleOptionSelect = (value: number) => {
    setAmountToRun(value);
  };

  const openRunSimulationModal = () => {
    if (!shareToken || !scenario?.id || !teamId) return;

    if (!canRunSimulations(amountToRun)) {
      return;
    }

    messageBus.publish('modal:run-figma-confidence-simulation:open', {
      teamId,
      scenarioId: scenario.id,
      shareToken,
      amount: amountToRun,
    });
  };

  const isSimulationInProgress = data?.reviewStatus === 'IN_PROGRESS';

  return (
    <PageContent>
      <ConfidenceContainer>
        <PageContent.Title title={t('prototypes:more_confidence.title')} />
        <Typography variant="body1">
          {t('prototypes:more_confidence.description')}
        </Typography>
        <Cards>
          <StyledCard>
            <IconContainer>
              <UsersIcon />
            </IconContainer>
            <StyledText>
              {t('prototypes:more_confidence.test_with_humans_text')}
            </StyledText>
            {scenario?.finalTriggerElement ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={testWithHumans}
                fullWidth
              >
                {t('prototypes:more_confidence.test_with_humans')}
              </Button>
            ) : (
              <Alert title={t('prototypes:more_confidence.set_up_human_testing_in_plugin')} severity="info" />
            )}
          </StyledCard>
          <StyledCard highlighted>
            <IconContainer>
              <RobotIcon />
            </IconContainer>
            <StyledText>
              {t('prototypes:more_confidence.run_extended_simulations_text')}
            </StyledText>
            {isSimulationInProgress ? (
              <InProgressInfo>
                <ProgressContainer>
                  <CircularProgress size={20} thickness={5} color="primary" />
                </ProgressContainer>
                {t('prototypes:more_confidence.simulation_in_progress')}
              </InProgressInfo>
            ) : null}
            <Options>
              {[1, 3, 10, 50, 100].map((val) => (
                <StyledOption
                  key={val}
                  active={val === amountToRun}
                  onClick={() => handleOptionSelect(val)}
                >
                  {val}
                </StyledOption>
              ))}
            </Options>
            {teamId && scenario?.projectId ? (
              <SimulationStepsLimitNotice
                teamId={teamId}
                workspaceId={scenario.projectId}
              />
            ) : null}
            <Button
              variant="contained"
              color="primary"
              onClick={openRunSimulationModal}
              fullWidth
              disabled={isSimulationInProgress || !scenario?.id || !teamId}
            >
              {t('prototypes:more_confidence.run_more_simulations', {
                count: amountToRun,
              })}
            </Button>
          </StyledCard>
        </Cards>
      </ConfidenceContainer>
    </PageContent>
  );
};
